import React, { useMemo } from "react";

import DropdownSelector from "../DropdownSelector";
import InfoTip from "../InfoTip";

export const CADENCE_DAY_OPTIONS = [90, 60] as const;
export const WINDOW_DAY_OPTIONS = [15, 30] as const;
export const SHORT_CYCLE_WINDOW_DAYS = 15;
export const WINDOW_OFFSET_DAY_OPTIONS = Array.from(
  { length: 31 },
  (_, i) => i
);
export const FORMAL_DUE_DATE_OPTIONS = [30, 45, 60];
export const REMINDER_DAY_OPTIONS = [1, 2, 3, 5, 7];
export const LEVEL_GAP_THRESHOLD_OPTIONS = [1, 2, 3];
export const RESOLUTION_DAY_OPTIONS = [3, 5, 7, 10, 14];
export const GRACE_EXTRA_DAY_OPTIONS = [0, 15, 30, 45, 60, 75, 90] as const;

const FIRST_BUSINESS_DAY = "first_business_day";

export interface AlAssessmentSettingsValues {
  cadenceDays: number;
  windowDays: number;
  /** null = first business day of the period */
  windowOffsetDays: number | null;
  formalDefaultDueDateDays: number;
  formalReminderDays: number;
  teamReminderDays: number;
  disconnectReminderDays: number;
  levelGapThreshold: number;
  resolutionDays: number;
  /** Extra days after the cycle length that a publish still counts an input. */
  graceExtraDays: number;
}

export const DEFAULT_AL_ASSESSMENT_SETTINGS: AlAssessmentSettingsValues = {
  cadenceDays: 90,
  windowDays: 15,
  windowOffsetDays: null,
  formalDefaultDueDateDays: 30,
  formalReminderDays: 2,
  teamReminderDays: 2,
  disconnectReminderDays: 2,
  levelGapThreshold: 1,
  resolutionDays: 5,
  graceExtraDays: 0,
};

export const pickAllowed = (
  value: unknown,
  allowed: readonly number[],
  fallback: number
): number => {
  const parsed = Number(value);
  return allowed.includes(parsed) ? parsed : fallback;
};

/** 30-day windows exist only on a 90-day cycle. */
export const allowedWindowDays = (cadenceDays: number, windowDays: unknown) =>
  cadenceDays === 90
    ? pickAllowed(windowDays, WINDOW_DAY_OPTIONS, SHORT_CYCLE_WINDOW_DAYS)
    : SHORT_CYCLE_WINDOW_DAYS;

export const parseLevelGapThreshold = (raw: unknown): number => {
  const text = typeof raw === "string" ? raw.trim().toUpperCase() : raw;
  const numeric =
    typeof text === "string" ? Number(text.replace(/^L/, "")) : Number(text);
  return pickAllowed(
    numeric,
    LEVEL_GAP_THRESHOLD_OPTIONS,
    DEFAULT_AL_ASSESSMENT_SETTINGS.levelGapThreshold
  );
};

const SECTION_TOOLTIPS = {
  calendar: {
    title: "Assessment Calendar",
    body: "Team windows open automatically at the start of each period. A 90-day cycle can use a 15 or 30 day window; a 60-day cycle always uses 15 days. Levels auto-publish the day after the window closes (UTC). Changes apply from the next period.",
  },
  due: {
    title: "Default Due Date",
    body: "Formal: prefill when creating or converting a Formal (30 to 60 days).",
  },
  email: {
    title: "Email Remainder",
    body: "Days before due to send Formal, Team, and Disconnect reminder emails.",
  },
  disconnect: {
    title: "Disconnect Detection",
    body: "When sponsor levels are far enough apart to flag a disconnect workflow, and how many days the Primary Sponsor has to resolve it.",
  },
  grace: {
    title: "Grace Period input",
    body: "How long after the cycle length a teammate's latest input is still included when an assessment publishes. My Update turns Stale at the cycle length alone.",
  },
} as const;

const toDayOptions = (values: readonly number[]) =>
  values.map(n => ({
    label: `${n} ${n === 1 ? "day" : "days"}`,
    value: String(n),
  }));

const SettingsSection: React.FC<{
  title: string;
  tooltip: { title: string; body: string };
  children: React.ReactNode;
}> = ({ title, tooltip, children }) => (
  <div className="space-y-3 py-5 first:pt-4">
    <div className="flex items-center gap-1.5">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <InfoTip label={title} title={tooltip.title} body={tooltip.body} />
    </div>
    <div className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  </div>
);

const Field: React.FC<{
  label: string;
  selected: string;
  options: { label: string; value: string }[];
  onSelect: (value: string) => void;
  disabled?: boolean;
}> = ({ label, selected, options, onSelect, disabled = false }) => (
  <DropdownSelector
    label={label}
    selected={selected}
    options={options}
    onSelect={onSelect}
    triggerStyle="field"
    enableScroll
    fullWidth
    disabled={disabled}
  />
);

interface AlAssessmentSettingsFormProps {
  values: AlAssessmentSettingsValues;
  onChange: (next: AlAssessmentSettingsValues) => void;
}

const AlAssessmentSettingsForm: React.FC<AlAssessmentSettingsFormProps> = ({
  values,
  onChange,
}) => {
  const cadenceOptions = useMemo(
    () => [
      { label: "Quarterly (90 days)", value: "90" },
      { label: "Every two months (60 days)", value: "60" },
    ],
    []
  );
  const calendarPeriodOptions = useMemo(
    () => [
      { label: "First business day", value: FIRST_BUSINESS_DAY },
      ...WINDOW_OFFSET_DAY_OPTIONS.map(n => ({
        label: `Day ${n + 1} of the period`,
        value: String(n),
      })),
    ],
    []
  );
  const reminderOptions = useMemo(
    () =>
      REMINDER_DAY_OPTIONS.map(n => ({
        label: `${n} day${n !== 1 ? "s" : ""} before`,
        value: String(n),
      })),
    []
  );
  const thresholdOptions = useMemo(
    () =>
      LEVEL_GAP_THRESHOLD_OPTIONS.map(n => ({
        label: `${n} level${n !== 1 ? "s" : ""}`,
        value: String(n),
      })),
    []
  );

  const set = <K extends keyof AlAssessmentSettingsValues>(
    key: K,
    value: AlAssessmentSettingsValues[K]
  ) => onChange({ ...values, [key]: value });

  const setCadence = (raw: string) => {
    const cadenceDays = pickAllowed(raw, CADENCE_DAY_OPTIONS, 90);
    onChange({
      ...values,
      cadenceDays,
      windowDays: allowedWindowDays(cadenceDays, values.windowDays),
    });
  };

  return (
    <div className="divide-y divide-gray-200">
      <SettingsSection
        title="Assessment calendar"
        tooltip={SECTION_TOOLTIPS.calendar}
      >
        <Field
          label="Cycle length"
          selected={String(values.cadenceDays)}
          options={cadenceOptions}
          onSelect={setCadence}
        />
        <Field
          label="Choose calendar period"
          selected={
            values.windowOffsetDays == null
              ? FIRST_BUSINESS_DAY
              : String(values.windowOffsetDays)
          }
          options={calendarPeriodOptions}
          onSelect={next =>
            set(
              "windowOffsetDays",
              next === FIRST_BUSINESS_DAY ? null : Number(next)
            )
          }
        />
        <Field
          label="Choose window days"
          selected={String(values.windowDays)}
          options={toDayOptions(WINDOW_DAY_OPTIONS)}
          disabled={values.cadenceDays !== 90}
          onSelect={next =>
            set("windowDays", allowedWindowDays(values.cadenceDays, next))
          }
        />
      </SettingsSection>

      <SettingsSection
        title="Grace Period input"
        tooltip={SECTION_TOOLTIPS.grace}
      >
        <Field
          label="Teammates inputs"
          selected={String(values.graceExtraDays)}
          options={GRACE_EXTRA_DAY_OPTIONS.map(days => ({
            label: days === 0 ? "0 days (default)" : `${days} days`,
            value: String(days),
          }))}
          onSelect={next =>
            set("graceExtraDays", pickAllowed(next, GRACE_EXTRA_DAY_OPTIONS, 0))
          }
        />
      </SettingsSection>

      <SettingsSection title="Default due date" tooltip={SECTION_TOOLTIPS.due}>
        <Field
          label="Formal default due (days)"
          selected={String(values.formalDefaultDueDateDays)}
          options={toDayOptions(FORMAL_DUE_DATE_OPTIONS)}
          onSelect={next =>
            set(
              "formalDefaultDueDateDays",
              pickAllowed(next, FORMAL_DUE_DATE_OPTIONS, 30)
            )
          }
        />
      </SettingsSection>

      <SettingsSection title="Email remainder" tooltip={SECTION_TOOLTIPS.email}>
        <Field
          label="Formal Reminder (days before due)"
          selected={String(values.formalReminderDays)}
          options={reminderOptions}
          onSelect={next =>
            set(
              "formalReminderDays",
              pickAllowed(next, REMINDER_DAY_OPTIONS, 2)
            )
          }
        />
        <Field
          label="Team Reminder (days before due)"
          selected={String(values.teamReminderDays)}
          options={reminderOptions}
          onSelect={next =>
            set("teamReminderDays", pickAllowed(next, REMINDER_DAY_OPTIONS, 2))
          }
        />
        <Field
          label="Disconnect Reminder (days before due)"
          selected={String(values.disconnectReminderDays)}
          options={reminderOptions}
          onSelect={next =>
            set(
              "disconnectReminderDays",
              pickAllowed(next, REMINDER_DAY_OPTIONS, 2)
            )
          }
        />
      </SettingsSection>

      <SettingsSection
        title="Disconnect detection"
        tooltip={SECTION_TOOLTIPS.disconnect}
      >
        <Field
          label="Level gap threshold"
          selected={String(values.levelGapThreshold)}
          options={thresholdOptions}
          onSelect={next =>
            set(
              "levelGapThreshold",
              pickAllowed(next, LEVEL_GAP_THRESHOLD_OPTIONS, 1)
            )
          }
        />
        <Field
          label="Resolution window (days)"
          selected={String(values.resolutionDays)}
          options={toDayOptions(RESOLUTION_DAY_OPTIONS)}
          onSelect={next =>
            set("resolutionDays", pickAllowed(next, RESOLUTION_DAY_OPTIONS, 5))
          }
        />
      </SettingsSection>
    </div>
  );
};

export default AlAssessmentSettingsForm;
